// missionRunUtils.js

import { generateKey } from 'utilities/Keys';
import { resolveSchemaFieldValue } from 'views/utils/fieldValueResolver';
import {
  MISSION_MESSAGE_DETAIL_TITLE_KEY,
  getMissionTaskPersonKey,
  getMissionTaskPersonLabel,
  MISSION_TASK_HANDLED_BY,
  normalizeMissionPeople,
  normalizeMissionTask,
} from 'missions/taskmodal/missionTaskModalUtils';
import {
  buildLegacyMissionTemplateKey,
  normalizeMissionTemplateList,
} from 'missions/missionTemplateUtils';
import { computeMissionTopologicalTaskOrder } from 'missions/scheduling/missionScheduleUtils';
import {
  buildWorkflowSpineEdges,
  getWorkflowIncomingEdges,
  getWorkflowOutgoingEdges,
  isWorkflowIncomingEdgeDead,
  isWorkflowIncomingEdgeSatisfied,
  migratePrerequisitesToWorkflowEdges,
  normalizeWorkflowEdges,
} from 'workflows/workflowGraphUtils';

const NODE_MISSION_STATUS = Object.freeze({
  BLOCKED: 'blocked',
  COMPLETE: 'complete',
  IN_PROGRESS: 'inProgress',
  NOT_STARTED: 'notStarted',
});

const NODE_MISSION_STATUS_LABELS = Object.freeze({
  [NODE_MISSION_STATUS.BLOCKED]: 'Blocked',
  [NODE_MISSION_STATUS.COMPLETE]: 'Complete',
  [NODE_MISSION_STATUS.IN_PROGRESS]: 'In Progress',
  [NODE_MISSION_STATUS.NOT_STARTED]: 'Not Started',
});

const NODE_MISSION_TASK_EVENT_TYPE = Object.freeze({
  BLOCKER_CLEARED: 'blockerCleared',
  BOT_FAILED: 'botFailed',
  BOT_RUNNING: 'botRunning',
  TASK_BLOCKED: 'taskBlocked',
  TASK_COMPLETE: 'taskComplete',
  TASK_IN_PROGRESS: 'taskInProgress',
  TASK_NUDGED: 'taskNudged',
  TASK_READY: 'taskReady',
  TASK_REOPENED: 'taskReopened',
  TASK_RESET: 'taskReset',
});

const MISSION_TASK_STICKY_STATUSES = new Set([
  'blocked',
  'complete',
  'failed',
  'inProgress',
  'running',
]);

/**
 * buildNodeMissionTemplateKey
 *
 * Builds a stable template key for the current type-scoped Mission template.
 *
 * @param {string} typeKey - Current node type key.
 * @returns {string} Stable Mission template key.
 */
function buildNodeMissionTemplateKey(typeKey) {
  return buildLegacyMissionTemplateKey(typeKey);
}

/**
 * normalizeMissionTimestamp
 *
 * Normalizes a stored Mission timestamp into a positive millisecond value.
 *
 * @param {number|string|null} value - Raw timestamp value.
 * @returns {number} Positive millisecond timestamp or 0.
 */
function normalizeMissionTimestamp(value) {
  const normalizedValue = Number(value || 0);
  return Number.isFinite(normalizedValue) && normalizedValue > 0 ? normalizedValue : 0;
}

/**
 * formatMissionDateTime
 *
 * Formats a timestamp for compact Mission metadata and task timing labels.
 *
 * @param {number|string|null} value - Millisecond timestamp.
 * @returns {string} Human-readable date/time label.
 */
function formatMissionDateTime(value) {
  const normalizedValue = normalizeMissionTimestamp(value);
  if (!normalizedValue) return '';

  try {
    return new Date(normalizedValue).toLocaleString();
  } catch (error) {
    return '';
  }
}

/**
 * getMissionTaskAssigneeLabels
 *
 * Returns the visible assignee labels for the current Mission task.
 *
 * @param {object|null} task - Mission run task.
 * @returns {string[]} Visible assignee labels.
 */
function getMissionTaskAssigneeLabels(task) {
  return (Array.isArray(task?.assignees) ? task.assignees : [])
    .map((assignee) => getMissionTaskPersonLabel(assignee))
    .filter(Boolean);
}

/**
 * buildMissionTaskNudgeDetail
 *
 * Builds the compact task-activity copy payload for a nudge event.
 *
 * @param {object|null} task - Mission run task.
 * @returns {string} Compact recipient label.
 */
function buildMissionTaskNudgeDetail(task) {
  const assigneeLabels = getMissionTaskAssigneeLabels(task);

  if (assigneeLabels.length === 0) return 'Owner';
  if (assigneeLabels.length === 1) return assigneeLabels[0];
  if (assigneeLabels.length === 2) return assigneeLabels.join(' and ');

  return `${assigneeLabels.length} people`;
}

/**
 * isMissionTaskAssignee
 *
 * Returns whether the current user is assigned to the Mission task.
 *
 * @param {object|null} task - Mission run task.
 * @param {object|null} user - Current signed-in user.
 * @returns {boolean} Whether the user is an assignee.
 */
function isMissionTaskAssignee(task, user) {
  const userKey = getMissionTaskPersonKey(user);
  if (!userKey) return false;

  return (Array.isArray(task?.assignees) ? task.assignees : [])
    .some((assignee) => getMissionTaskPersonKey(assignee) === userKey);
}

/**
 * canNudgeMissionTask
 *
 * Returns whether the current task is actionable for a manual human-task nudge.
 *
 * @param {object|null} task - Mission run task.
 * @param {object|null} user - Current signed-in user.
 * @returns {boolean} Whether the task can be nudged.
 */
function canNudgeMissionTask(task, user) {
  const status = String(task?.status || '').trim();

  return task?.handledBy === MISSION_TASK_HANDLED_BY.PEOPLE
    && Array.isArray(task?.assignees)
    && task.assignees.length > 0
    && (status === 'ready' || status === 'inProgress')
    && !isMissionTaskAssignee(task, user);
}

/**
 * canMarkCompleteMissionTask
 *
 * Returns whether the current human Mission task should show the primary Mark Complete action.
 *
 * @param {object|null} task - Mission run task.
 * @returns {boolean} Whether the task can be completed directly from the inline action row.
 */
function canMarkCompleteMissionTask(task) {
  const status = String(task?.status || '').trim();

  return task?.handledBy === MISSION_TASK_HANDLED_BY.PEOPLE
    && (status === 'ready' || status === 'inProgress');
}

/**
 * canBlockMissionTask
 *
 * Returns whether the current human Mission task should expose the explicit blocked action.
 *
 * @param {object|null} task - Mission run task.
 * @returns {boolean} Whether the task can be marked blocked from the inline action row.
 */
function canBlockMissionTask(task) {
  const status = String(task?.status || '').trim();

  return task?.handledBy === MISSION_TASK_HANDLED_BY.PEOPLE
    && (status === 'ready' || status === 'inProgress');
}

/**
 * canUnblockMissionTask
 *
 * Returns whether the current human Mission task should expose the clear-blocker action.
 *
 * @param {object|null} task - Mission run task.
 * @returns {boolean} Whether the task can be unblocked from the inline action row.
 */
function canUnblockMissionTask(task) {
  const status = String(task?.status || '').trim();

  return task?.handledBy === MISSION_TASK_HANDLED_BY.PEOPLE
    && status === 'blocked';
}

/**
 * canReopenMissionTask
 *
 * Returns whether a completed people task can be reopened for another work cycle.
 *
 * @param {object|null} task - Mission run task.
 * @returns {boolean} Whether the task can be reopened.
 */
function canReopenMissionTask(task) {
  const status = String(task?.status || '').trim();

  return task?.handledBy === MISSION_TASK_HANDLED_BY.PEOPLE
    && status === 'complete';
}

/**
 * isMissionTaskAwaitingHumanAction
 *
 * Returns whether a people task currently needs human attention.
 *
 * @param {object|null} task - Mission run task.
 * @returns {boolean} Whether the task is awaiting human action.
 */
function isMissionTaskAwaitingHumanAction(task) {
  if (task?.handledBy !== MISSION_TASK_HANDLED_BY.PEOPLE) {
    return false;
  }

  const status = String(task?.status || '').trim();

  return status === 'ready' || status === 'inProgress' || status === 'blocked';
}

/**
 * findFirstHumanActionableTaskInPrerequisiteChain
 *
 * Returns the earliest prerequisite-chain task that is awaiting human action.
 *
 * @param {object[]} tasks - Mission run tasks.
 * @returns {object|null} Focus task or null.
 */
function findFirstHumanActionableTaskInPrerequisiteChain(tasks = [], edges = null) {
  const { hasCycle, order, taskByKey } = computeMissionTopologicalTaskOrder(tasks, edges);

  if (hasCycle) {
    return null;
  }

  for (const taskKey of order) {
    const task = taskByKey.get(taskKey);

    if (isMissionTaskAwaitingHumanAction(task)) {
      return task;
    }
  }

  return null;
}

/**
 * canProcessMissionBotTask
 *
 * Returns whether the current Mission Bot task should be handed to backend execution.
 *
 * @param {object|null} task - Mission run task.
 * @returns {boolean} Whether the Bot task should be processed.
 */
function canProcessMissionBotTask(task) {
  const status = String(task?.status || '').trim();

  return task?.handledBy === MISSION_TASK_HANDLED_BY.BOT
    && (status === 'ready' || status === 'running');
}

/**
 * formatMissionUpdatedAt
 *
 * Formats a timestamp for the compact Mission card metadata.
 *
 * @param {number|string|null} updatedAtMs - Millisecond timestamp.
 * @returns {string} Human-readable timestamp label.
 */
function formatMissionUpdatedAt(updatedAtMs) {
  return formatMissionDateTime(updatedAtMs) || 'Not launched yet';
}

function getMissionTaskStatusPresentation(theme, task) {
  const status = String(task?.status || '').trim();

  if (status === 'complete') {
    return {
      backgroundColor: theme.green,
      color: theme.highlightForeground,
      iconKey: 'check_circle',
      iconColor: theme.green,
      label: 'Complete',
    };
  }

  if (status === 'blocked') {
    return {
      backgroundColor: theme.red,
      color: theme.highlightForeground,
      iconKey: 'block',
      iconColor: theme.red,
      label: 'Blocked',
    };
  }

  if (status === 'failed') {
    return {
      backgroundColor: theme.red,
      color: theme.highlightForeground,
      iconKey: 'block',
      iconColor: theme.red,
      label: 'Failed',
    };
  }

  if (status === 'inProgress') {
    return {
      backgroundColor: theme.blue,
      color: theme.highlightForeground,
      iconKey: 'schedule',
      iconColor: theme.blue,
      label: 'In Progress',
    };
  }

  if (status === 'running') {
    return {
      backgroundColor: theme.blue,
      color: theme.highlightForeground,
      iconKey: 'schedule',
      iconColor: theme.blue,
      label: 'Running',
    };
  }

  if (status === 'waiting') {
    return {
      backgroundColor: theme.backgroundHover,
      color: theme.foregroundMuted,
      iconKey: 'hourglass',
      iconColor: theme.foregroundMuted,
      label: 'Coming Up...',
    };
  }

  if (status === 'skipped') {
    return {
      backgroundColor: theme.backgroundHover,
      color: theme.foregroundMuted,
      iconKey: 'keyboard_double_arrow_right',
      iconColor: theme.foregroundMuted,
      label: 'Skipped',
    };
  }

  return {
    backgroundColor: theme.blue,
    color: theme.highlightForeground,
    iconKey: 'play_arrow',
    iconColor: theme.blue,
    label: 'Awaiting Action',
  };
}

/**
 * buildMissionTaskInitialStatus
 *
 * Chooses the first execution status for a launched task.
 *
 * @param {object} task - Normalized Mission template task.
 * @returns {string} Initial task status.
 */
function buildMissionTaskInitialStatus(task) {
  if (task?.handledBy === MISSION_TASK_HANDLED_BY.BOT) return 'ready';
  return 'ready';
}

/**
 * isMissionSendMessageTask
 *
 * Returns whether the current Mission task is a Bot Send Message task.
 *
 * @param {object|null} task - Mission task candidate.
 * @returns {boolean} Whether the task is a Send Message Bot task.
 */
function isMissionSendMessageTask(task) {
  return task?.handledBy === MISSION_TASK_HANDLED_BY.BOT
    && String(task?.missionBot?.job || task?.botJob || '').trim() === 'sendMessage';
}

/**
 * getMissionRunTaskLaunchRecipient
 *
 * Returns the launch-time recipient snapshot stored on the Mission run task.
 *
 * @param {object|null} task - Mission run task.
 * @returns {object|null} Normalized selected space member.
 */
function getMissionRunTaskLaunchRecipient(task) {
  return normalizeMissionPeople(task?.runConfig?.sendMessageRecipient)[0] || null;
}

/**
 * normalizeMissionTaskEvents
 *
 * Normalizes persisted Mission task events into a compact append-only list.
 *
 * @param {object[]|null} events - Raw persisted task events.
 * @returns {object[]} Normalized Mission task events.
 */
function normalizeMissionTaskEvents(events) {
  return (Array.isArray(events) ? events : [])
    .map((event) => {
      const createdAtMs = Number(event?.createdAtMs || 0) || 0;
      const key = String(event?.key || '').trim();
      const taskKey = String(event?.taskKey || '').trim();
      const type = String(event?.type || '').trim();

      if (!key || !taskKey || !type || !createdAtMs || type === 'taskOverdue') return null;

      const actorUserKey = String(event?.actorUserKey || '').trim();
      const actorName = String(event?.actorName || '').trim();

      return {
        createdAtMs,
        detail: String(event?.detail || '').trim(),
        key,
        taskKey,
        taskTitle: String(event?.taskTitle || '').trim(),
        type,
        ...(actorUserKey ? { actorUserKey } : {}),
        ...(actorName ? { actorName } : {}),
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.createdAtMs - right.createdAtMs);
}

/**
 * buildMissionTaskEvent
 *
 * Creates a persisted Mission task event entry.
 *
 * @param {object} options - Event inputs.
 * @param {number} options.createdAtMs - Event creation time.
 * @param {object} options.task - Task associated with the event.
 * @param {string} options.detail - Optional event detail text.
 * @param {string} options.type - Event type key.
 * @returns {object} Persistable Mission task event.
 */
function buildMissionTaskEvent({
  actorName = '',
  actorUserKey = '',
  createdAtMs,
  detail = '',
  task,
  type,
}) {
  const normalizedActorUserKey = String(actorUserKey || '').trim();
  const normalizedActorName = String(actorName || '').trim();

  return {
    createdAtMs,
    detail: String(detail || task?.blockingReason || '').trim(),
    key: generateKey(),
    taskKey: String(task?.key || '').trim(),
    taskTitle: String(task?.title || '').trim(),
    type,
    ...(normalizedActorUserKey ? { actorUserKey: normalizedActorUserKey } : {}),
    ...(normalizedActorName ? { actorName: normalizedActorName } : {}),
  };
}

/**
 * buildMissionTaskActivityFromEvent
 *
 * Maps a persisted task event to the compact latest-activity line.
 *
 * @param {object|null} event - Latest task event.
 * @returns {string} Compact task activity copy.
 */
function buildMissionTaskActivityFromEvent(event) {
  if (!event) return '';

  if (event.type === NODE_MISSION_TASK_EVENT_TYPE.BLOCKER_CLEARED) {
    return 'Blocker was cleared.';
  }

  if (event.type === NODE_MISSION_TASK_EVENT_TYPE.BOT_FAILED) {
    return event.detail ? `Bot failed: ${event.detail}` : 'Bot failed.';
  }

  if (event.type === NODE_MISSION_TASK_EVENT_TYPE.BOT_RUNNING) {
    return 'Bot started running.';
  }

  if (event.type === NODE_MISSION_TASK_EVENT_TYPE.TASK_BLOCKED) {
    return event.detail ? `Task was marked Blocked: ${event.detail}` : 'Task was marked Blocked.';
  }

  if (event.type === NODE_MISSION_TASK_EVENT_TYPE.TASK_COMPLETE) {
    return 'Task reached Complete.';
  }

  if (event.type === NODE_MISSION_TASK_EVENT_TYPE.TASK_IN_PROGRESS) {
    return 'Task moved to In Progress.';
  }

  if (event.type === NODE_MISSION_TASK_EVENT_TYPE.TASK_NUDGED) {
    return event.detail ? `${event.detail} nudged, awaiting response.` : 'Task owner was nudged.';
  }

  if (event.type === NODE_MISSION_TASK_EVENT_TYPE.TASK_READY) {
    return 'Task became Ready.';
  }

  if (event.type === NODE_MISSION_TASK_EVENT_TYPE.TASK_REOPENED) {
    return 'Task was reopened.';
  }

  if (event.type === NODE_MISSION_TASK_EVENT_TYPE.TASK_RESET) {
    return event.detail
      ? `Task was reset after ${event.detail} reopened.`
      : 'Task was reset after an upstream step reopened.';
  }

  return '';
}

/**
 * getMissionTaskEvents
 *
 * Returns the append-only event history for a single task in reverse chronological order.
 *
 * @param {object[]} taskEvents - Persisted Mission task events.
 * @param {string} taskKey - Target task key.
 * @returns {object[]} Task event history.
 */
function getMissionTaskEvents(taskEvents, taskKey) {
  const normalizedTaskKey = String(taskKey || '').trim();
  if (!normalizedTaskKey) return [];

  return normalizeMissionTaskEvents(taskEvents)
    .filter((event) => event.taskKey === normalizedTaskKey)
    .slice()
    .reverse();
}

/**
 * formatMissionTaskEventTime
 *
 * Formats a persisted Mission task event time for the task history list.
 *
 * @param {number|string|null} createdAtMs - Event timestamp.
 * @returns {string} Human-readable event time.
 */
function formatMissionTaskEventTime(createdAtMs) {
  const normalizedValue = Number(createdAtMs || 0);
  if (!Number.isFinite(normalizedValue) || normalizedValue <= 0) return '';

  try {
    return new Date(normalizedValue).toLocaleString();
  } catch (error) {
    return '';
  }
}

/**
 * resolveWorkflowRunEdges
 *
 * Prefers persisted run/template edges; falls back to prereq migration or spine.
 *
 * @param {object[]} tasks
 * @param {object[]|null|undefined} edges
 * @returns {object[]}
 */
function resolveWorkflowRunEdges(tasks, edges) {
  const normalized = normalizeWorkflowEdges(edges);
  if (normalized.length > 0) return normalized;

  const fromPrereqs = migratePrerequisitesToWorkflowEdges(tasks);
  if (fromPrereqs.length > 0) return normalizeWorkflowEdges(fromPrereqs);

  return normalizeWorkflowEdges(buildWorkflowSpineEdges(tasks));
}

/**
 * getMissionWaitingOnTasks
 *
 * Resolves upstream steps that still block this task on the workflow graph.
 *
 * @param {object} task - Normalized Mission run task.
 * @param {Map<string, object>} taskMap - Current task lookup keyed by task key.
 * @param {object[]} edges - Workflow edges for the run.
 * @returns {object[]} Incomplete upstream tasks.
 */
function getMissionWaitingOnTasks(task, taskMap, edges) {
  const incoming = getWorkflowIncomingEdges(edges, task?.key);
  if (incoming.length === 0) return [];

  return incoming
    .filter((edge) => !isWorkflowIncomingEdgeSatisfied(edge, taskMap))
    .filter((edge) => !isWorkflowIncomingEdgeDead(edge, taskMap))
    .map((edge) => taskMap.get(edge.fromTaskKey))
    .filter(Boolean);
}

/**
 * deriveMissionTaskStatus
 *
 * Applies edge-driven waiting / ready / skipped rules to the current task.
 *
 * @param {object} task - Normalized Mission run task.
 * @param {Map<string, object>} taskMap - Current task lookup keyed by task key.
 * @param {object[]} edges - Workflow edges for the run.
 * @returns {string} Effective task status.
 */
function deriveMissionTaskStatus(task, taskMap, edges) {
  const storedStatus = String(task?.status || '').trim() || buildMissionTaskInitialStatus(task);

  if (
    storedStatus === 'complete'
    || storedStatus === 'failed'
    || storedStatus === 'blocked'
    || storedStatus === 'inProgress'
    || storedStatus === 'running'
  ) {
    return storedStatus;
  }

  const incoming = getWorkflowIncomingEdges(edges, task?.key);
  if (incoming.length === 0) {
    return 'ready';
  }

  if (incoming.every((edge) => isWorkflowIncomingEdgeDead(edge, taskMap))) {
    return 'skipped';
  }

  if (incoming.some((edge) => isWorkflowIncomingEdgeSatisfied(edge, taskMap))) {
    return 'ready';
  }

  return 'waiting';
}

/**
 * buildMissionTaskActivityLine
 *
 * Derives the compact activity line shown for a task row.
 *
 * @param {object} task - Normalized Mission run task.
 * @returns {string} Task activity summary.
 */
function buildMissionTaskActivityLine(task) {
  const status = String(task?.status || '').trim();
  const actorLabel = task?.handledBy === MISSION_TASK_HANDLED_BY.BOT ? 'Bot' : 'Assigned people';
  const blockingReason = String(task?.blockingReason || '').trim();
  const failureDetail = String(task?.failureDetail || '').trim();
  const waitingOnTitles = Array.isArray(task?.waitingOnTaskTitles) ? task.waitingOnTaskTitles.filter(Boolean) : [];

  if (status === 'complete') {
    const executionSummary = String(task?.executionSummary || '').trim();
    if (executionSummary) return executionSummary;
    return `${actorLabel} completed this task.`;
  }
  if (status === 'blocked') return blockingReason ? `Blocked: ${blockingReason}` : 'Work is blocked and needs attention.';
  if (status === 'failed') return failureDetail ? `Bot failed: ${failureDetail}` : 'Bot execution failed.';
  if (status === 'running') return 'Bot is running now.';
  if (status === 'inProgress') return 'People are actively working this task.';
  if (status === 'skipped') {
    return 'Skipped — not on the path taken for this playbook.';
  }
  if (status === 'waiting') {
    return waitingOnTitles.length > 0
      ? `Waiting on ${waitingOnTitles.join(', ')}.`
      : 'Waiting on upstream steps.';
  }
  return task?.handledBy === MISSION_TASK_HANDLED_BY.BOT
    ? 'Ready for Bot execution.'
    : 'Ready for assigned people.';
}

/**
 * getLatestMissionTaskEventByTask
 *
 * Builds a lookup of the most recent event for each task key.
 *
 * @param {object[]} taskEvents - Persisted Mission task events.
 * @returns {Map<string, object>} Latest event keyed by task key.
 */
function getLatestMissionTaskEventByTask(taskEvents) {
  const latestEvents = new Map();

  normalizeMissionTaskEvents(taskEvents).forEach((event) => {
    latestEvents.set(event.taskKey, event);
  });

  return latestEvents;
}

/**
 * applyMissionTaskActivity
 *
 * Derives the latest activity line from persisted task events when available.
 *
 * @param {object[]} tasks - Normalized Mission run tasks.
 * @param {object[]} taskEvents - Persisted Mission task events.
 * @returns {object[]} Tasks with activity lines applied.
 */
function applyMissionTaskActivity(tasks, taskEvents) {
  const latestEventsByTask = getLatestMissionTaskEventByTask(taskEvents);

  return (Array.isArray(tasks) ? tasks : []).map((task) => {
    const latestEvent = latestEventsByTask.get(task.key) || null;
    const latestActivity = buildMissionTaskActivityFromEvent(latestEvent);

    return {
      ...task,
      latestActivity: latestActivity || buildMissionTaskActivityLine(task),
    };
  });
}

/**
 * recomputeMissionRunTasks
 *
 * Re-applies edge-driven waiting, readiness, and skipped rules across a run.
 *
 * @param {object[]} tasks - Current raw Mission run tasks.
 * @param {object[]|null} [edges] - Workflow edges (run or template).
 * @returns {object[]} Normalized Mission run tasks.
 */
function recomputeMissionRunTasks(tasks, edges = null) {
  const nowMs = Date.now();
  const resolvedEdges = resolveWorkflowRunEdges(tasks, edges);
  let nextTasks = (Array.isArray(tasks) ? tasks : [])
    .map((task) => {
      const normalizedTask = normalizeMissionTask(task);
      const completionOutcomeKey = String(task?.completionOutcomeKey || '').trim();

      return {
        ...task,
        ...normalizedTask,
        blockingReason: String(task?.blockingReason || '').trim(),
        ...(completionOutcomeKey ? { completionOutcomeKey } : {}),
        readyAtMs: normalizeMissionTimestamp(task?.readyAtMs),
        status: String(task?.status || '').trim() || buildMissionTaskInitialStatus(normalizedTask),
        updatedAtMs: Number(task?.updatedAtMs || 0) || Date.now(),
      };
    })
    .filter((task) => String(task?.key || '').trim());

  // Multi-pass so skipped upstream can mark dead branches downstream.
  for (let pass = 0; pass < nextTasks.length + 1; pass += 1) {
    const taskMap = new Map(nextTasks.map((task) => [task.key, task]));
    let changed = false;

    nextTasks = nextTasks.map((task) => {
      const waitingOnTasks = getMissionWaitingOnTasks(task, taskMap, resolvedEdges);
      const waitingOnTaskTitles = waitingOnTasks
        .map((entry) => String(entry?.title || '').trim())
        .filter(Boolean);
      const status = deriveMissionTaskStatus(task, taskMap, resolvedEdges);
      const previousStatus = String(task?.status || '').trim();
      if (status !== previousStatus) changed = true;

      const existingReadyAtMs = normalizeMissionTimestamp(task?.readyAtMs);
      const becameActionable = (
        status === 'ready'
        || status === 'inProgress'
        || status === 'running'
      ) && (previousStatus === 'waiting' || previousStatus === 'skipped' || previousStatus === '');
      const readyAtMs = (status === 'waiting' || status === 'skipped')
        ? 0
        : existingReadyAtMs || (becameActionable ? nowMs : existingReadyAtMs);

      return {
        ...task,
        readyAtMs,
        status,
        waitingOnTaskKeys: waitingOnTasks.map((entry) => entry.key),
        waitingOnTaskTitles,
      };
    });

    if (!changed) break;
  }

  return nextTasks;
}

/**
 * collectTransitiveDependentTaskKeys
 *
 * Returns task keys reachable downstream from `rootTaskKey` via workflow edges.
 *
 * @param {object[]} tasks - Mission run tasks.
 * @param {string} rootTaskKey - Upstream task key being reopened.
 * @param {object[]|null} [edges] - Workflow edges for the run.
 * @returns {Set<string>} Dependent task keys.
 */
function collectTransitiveDependentTaskKeys(tasks, rootTaskKey, edges = null) {
  const normalizedRootKey = String(rootTaskKey || '').trim();
  const dependents = new Set();
  if (!normalizedRootKey) return dependents;

  const resolvedEdges = resolveWorkflowRunEdges(tasks, edges);
  const queue = [normalizedRootKey];

  while (queue.length > 0) {
    const current = queue.shift();
    getWorkflowOutgoingEdges(resolvedEdges, current).forEach((edge) => {
      const nextKey = String(edge?.toTaskKey || '').trim();
      if (!nextKey || nextKey === normalizedRootKey || dependents.has(nextKey)) return;
      dependents.add(nextKey);
      queue.push(nextKey);
    });
  }

  return dependents;
}

/**
 * collectMissionTaskReopenCascadeTasks
 *
 * Preview of later tasks that would change status when `taskKey` is reopened.
 *
 * @param {object|null} missionRun - Current Mission run.
 * @param {string} taskKey - Task being reopened.
 * @returns {object[]} Tasks that would leave their current status.
 */
function collectMissionTaskReopenCascadeTasks(missionRun, taskKey) {
  const normalizedTaskKey = String(taskKey || '').trim();
  const tasks = Array.isArray(missionRun?.tasks) ? missionRun.tasks : [];
  const targetTask = tasks.find((task) => String(task?.key || '').trim() === normalizedTaskKey) || null;
  if (!canReopenMissionTask(targetTask)) return [];

  const runEdges = resolveWorkflowRunEdges(tasks, missionRun?.edges);
  const dependentKeys = collectTransitiveDependentTaskKeys(tasks, normalizedTaskKey, runEdges);
  if (dependentKeys.size === 0) return [];

  const patchedTasks = tasks.map((task) => {
    const key = String(task?.key || '').trim();
    if (key === normalizedTaskKey) {
      const next = {
        ...task,
        blockingReason: '',
        failureDetail: '',
        status: 'ready',
      };
      delete next.completionOutcomeKey;
      return next;
    }

    if (dependentKeys.has(key) && (
      MISSION_TASK_STICKY_STATUSES.has(String(task?.status || '').trim())
      || String(task?.status || '').trim() === 'skipped'
    )) {
      const next = {
        ...task,
        blockingReason: '',
        failureDetail: '',
        readyAtMs: 0,
        status: 'ready',
      };
      delete next.completionOutcomeKey;
      return next;
    }

    return task;
  });

  const recomputedTasks = recomputeMissionRunTasks(patchedTasks, runEdges);
  const previousByKey = new Map(
    tasks.map((task) => [String(task?.key || '').trim(), task])
  );

  return recomputedTasks.filter((task) => {
    const key = String(task?.key || '').trim();
    if (!dependentKeys.has(key)) return false;
    const previousStatus = String(previousByKey.get(key)?.status || '').trim();
    const nextStatus = String(task?.status || '').trim();
    return previousStatus !== nextStatus;
  });
}

/**
 * buildReopenMissionTaskConfirmMessage
 *
 * @param {object|null} task - Task being reopened.
 * @param {object[]} cascadeTasks - Downstream tasks that will reset.
 * @returns {string} Confirm dialog copy.
 */
function buildReopenMissionTaskConfirmMessage(task, cascadeTasks = []) {
  const title = String(task?.title || '').trim() || 'this step';
  const cascadeList = Array.isArray(cascadeTasks) ? cascadeTasks : [];

  if (cascadeList.length === 0) {
    return `Reopen ${title}?`;
  }

  const names = cascadeList
    .map((entry) => String(entry?.title || '').trim())
    .filter(Boolean);
  const preview = names.slice(0, 4).join(', ');
  const overflow = names.length > 4 ? ` and ${names.length - 4} more` : '';

  return `Reopen ${title} and reset later steps that depended on it (${preview}${overflow})?`;
}

/**
 * countMissionTaskEventsByType
 *
 * @param {object[]} taskEvents - Persisted Mission task events.
 * @param {string} taskKey - Target task key.
 * @param {string} eventType - Event type key.
 * @returns {number} Matching event count.
 */
function countMissionTaskEventsByType(taskEvents, taskKey, eventType) {
  const normalizedTaskKey = String(taskKey || '').trim();
  const normalizedType = String(eventType || '').trim();
  if (!normalizedTaskKey || !normalizedType) return 0;

  return normalizeMissionTaskEvents(taskEvents)
    .filter((event) => (
      event.taskKey === normalizedTaskKey
      && String(event?.type || '').trim() === normalizedType
    ))
    .length;
}

/**
 * getMissionTaskRevisionLabel
 *
 * Revision chip for steps that have been through more than one work cycle.
 *
 * @param {object[]} taskEvents - Persisted Mission task events.
 * @param {object|null} task - Mission run task.
 * @returns {string} e.g. "Rev 2", or empty when still on the first cycle.
 */
function getMissionTaskRevisionLabel(taskEvents, task) {
  const taskKey = String(task?.key || '').trim();
  if (!taskKey) return '';

  const completeCount = countMissionTaskEventsByType(
    taskEvents,
    taskKey,
    NODE_MISSION_TASK_EVENT_TYPE.TASK_COMPLETE
  );
  const reopenCount = countMissionTaskEventsByType(
    taskEvents,
    taskKey,
    NODE_MISSION_TASK_EVENT_TYPE.TASK_REOPENED
  );

  if (completeCount < 1 && reopenCount < 1) return '';
  if (completeCount < 2 && reopenCount < 1) return '';

  const status = String(task?.status || '').trim();
  const revision = status === 'complete'
    ? Math.max(completeCount, 1)
    : Math.max(completeCount + 1, reopenCount + 1);

  return `Rev ${revision}`;
}

/**
 * reopenMissionRunTask
 *
 * Reopens a completed people task and cascades dependent later steps back to waiting.
 *
 * @param {object} options
 * @param {object|null} options.missionRun - Current Mission run.
 * @param {number} [options.nowMs]
 * @param {string} options.taskKey - Completed task to reopen.
 * @returns {{ cascadeTasks: object[], taskEvents: object[], tasks: object[] } | null}
 */
function reopenMissionRunTask({
  missionRun = null,
  nowMs = Date.now(),
  taskKey = '',
} = {}) {
  const normalizedTaskKey = String(taskKey || '').trim();
  const previousTasks = Array.isArray(missionRun?.tasks) ? missionRun.tasks : [];
  const targetTask = previousTasks.find((task) => String(task?.key || '').trim() === normalizedTaskKey) || null;
  if (!canReopenMissionTask(targetTask)) return null;

  const runEdges = resolveWorkflowRunEdges(previousTasks, missionRun?.edges);
  const dependentKeys = collectTransitiveDependentTaskKeys(previousTasks, normalizedTaskKey, runEdges);
  const previousByKey = new Map(
    previousTasks.map((task) => [String(task?.key || '').trim(), task])
  );

  const patchedTasks = previousTasks.map((task) => {
    const key = String(task?.key || '').trim();

    if (key === normalizedTaskKey) {
      const next = {
        ...task,
        blockingReason: '',
        failureDetail: '',
        readyAtMs: nowMs,
        status: 'ready',
        updatedAtMs: nowMs,
      };
      delete next.completionOutcomeKey;
      return next;
    }

    if (dependentKeys.has(key) && (
      MISSION_TASK_STICKY_STATUSES.has(String(task?.status || '').trim())
      || String(task?.status || '').trim() === 'skipped'
    )) {
      const next = {
        ...task,
        blockingReason: '',
        failureDetail: '',
        readyAtMs: 0,
        status: 'ready',
        updatedAtMs: nowMs,
      };
      delete next.completionOutcomeKey;
      return next;
    }

    return task;
  });

  const recomputedTasks = recomputeMissionRunTasks(patchedTasks, runEdges);
  const reopenedTask = recomputedTasks.find((task) => task.key === normalizedTaskKey) || targetTask;
  const appendedEvents = [
    buildMissionTaskEvent({
      createdAtMs: nowMs,
      task: reopenedTask,
      type: NODE_MISSION_TASK_EVENT_TYPE.TASK_REOPENED,
    }),
  ];
  const cascadeTasks = [];

  recomputedTasks.forEach((task) => {
    const key = String(task?.key || '').trim();
    if (!dependentKeys.has(key)) return;

    const previousStatus = String(previousByKey.get(key)?.status || '').trim();
    const nextStatus = String(task?.status || '').trim();
    if (previousStatus === nextStatus) return;

    cascadeTasks.push(task);
    appendedEvents.push(buildMissionTaskEvent({
      createdAtMs: nowMs,
      detail: String(targetTask?.title || '').trim(),
      task,
      type: NODE_MISSION_TASK_EVENT_TYPE.TASK_RESET,
    }));
  });

  const nextTaskEvents = [
    ...normalizeMissionTaskEvents(missionRun?.taskEvents),
    ...appendedEvents,
  ];

  return {
    cascadeTasks,
    taskEvents: nextTaskEvents,
    tasks: applyMissionTaskActivity(recomputedTasks, nextTaskEvents),
  };
}

/**
 * appendDerivedMissionTaskEvents
 *
 * Appends task events when prerequisite-derived status changes unlock a task.
 *
 * @param {object} options - Event reconciliation inputs.
 * @param {number} options.createdAtMs - Event creation time.
 * @param {object[]} options.nextTasks - Recomputed next Mission tasks.
 * @param {object[]} options.previousTaskEvents - Existing persisted task events.
 * @param {object[]} options.previousTasks - Previous normalized Mission tasks.
 * @returns {object[]} Updated append-only task event list.
 */
function appendDerivedMissionTaskEvents({
  createdAtMs,
  nextTasks,
  previousTaskEvents,
  previousTasks,
}) {
  const nextTaskList = Array.isArray(nextTasks) ? nextTasks : [];
  const previousTaskList = Array.isArray(previousTasks) ? previousTasks : [];
  const priorEvents = normalizeMissionTaskEvents(previousTaskEvents);
  const previousTaskMap = new Map(previousTaskList.map((task) => [String(task?.key || '').trim(), task]));
  const appendedEvents = [];

  nextTaskList.forEach((task) => {
    const previousTask = previousTaskMap.get(String(task?.key || '').trim()) || null;
    if (!previousTask) return;

    if (String(previousTask?.status || '').trim() === 'waiting' && String(task?.status || '').trim() === 'ready') {
      appendedEvents.push(buildMissionTaskEvent({
        createdAtMs,
        task,
        type: NODE_MISSION_TASK_EVENT_TYPE.TASK_READY,
      }));
    }
  });

  return [...priorEvents, ...appendedEvents];
}

/**
 * appendStatusTransitionTaskEvents
 *
 * Appends explicit task events for user-driven status transitions on a single task.
 *
 * @param {object} options - Event reconciliation inputs.
 * @param {number} options.createdAtMs - Event creation time.
 * @param {object|null} options.nextTask - Updated task after recompute.
 * @param {object[]} options.previousTaskEvents - Existing persisted task events.
 * @param {object|null} options.previousTask - Previous task before the status change.
 * @returns {object[]} Updated append-only task event list.
 */
function appendStatusTransitionTaskEvents({
  createdAtMs,
  nextTask,
  previousTaskEvents,
  previousTask,
}) {
  const priorEvents = normalizeMissionTaskEvents(previousTaskEvents);
  const existingTask = previousTask && typeof previousTask === 'object' ? previousTask : null;
  const updatedTask = nextTask && typeof nextTask === 'object' ? nextTask : null;
  const previousStatus = String(existingTask?.status || '').trim();
  const nextStatus = String(updatedTask?.status || '').trim();
  const appendedEvents = [];

  if (!updatedTask || !nextStatus || previousStatus === nextStatus) {
    return priorEvents;
  }

  if (previousStatus === 'blocked' && nextStatus !== 'blocked') {
    appendedEvents.push(buildMissionTaskEvent({
      createdAtMs,
      detail: previousTask?.blockingReason,
      task: updatedTask,
      type: NODE_MISSION_TASK_EVENT_TYPE.BLOCKER_CLEARED,
    }));
  }

  if (nextStatus === 'inProgress') {
    appendedEvents.push(buildMissionTaskEvent({
      createdAtMs,
      task: updatedTask,
      type: NODE_MISSION_TASK_EVENT_TYPE.TASK_IN_PROGRESS,
    }));
  }

  if (nextStatus === 'blocked') {
    appendedEvents.push(buildMissionTaskEvent({
      createdAtMs,
      detail: updatedTask?.blockingReason,
      task: updatedTask,
      type: NODE_MISSION_TASK_EVENT_TYPE.TASK_BLOCKED,
    }));
  }

  if (nextStatus === 'running') {
    appendedEvents.push(buildMissionTaskEvent({
      createdAtMs,
      task: updatedTask,
      type: NODE_MISSION_TASK_EVENT_TYPE.BOT_RUNNING,
    }));
  }

  if (nextStatus === 'failed') {
    appendedEvents.push(buildMissionTaskEvent({
      createdAtMs,
      detail: updatedTask?.failureDetail,
      task: updatedTask,
      type: NODE_MISSION_TASK_EVENT_TYPE.BOT_FAILED,
    }));
  }

  if (nextStatus === 'complete') {
    appendedEvents.push(buildMissionTaskEvent({
      createdAtMs,
      task: updatedTask,
      type: NODE_MISSION_TASK_EVENT_TYPE.TASK_COMPLETE,
    }));
  }

  return [...priorEvents, ...appendedEvents];
}

/**
 * buildMissionRunTask
 *
 * Converts a Mission template task into its first node-scoped run task shape.
 *
 * @param {object} task - Mission template task.
 * @returns {object} Node-scoped Mission run task.
 */
function buildMissionRunTask(task, options = {}) {
  const normalizedTask = normalizeMissionTask(task);
  const status = buildMissionTaskInitialStatus(normalizedTask);
  const existingRunConfig = normalizedTask?.runConfig && typeof normalizedTask.runConfig === 'object'
    ? normalizedTask.runConfig
    : {};
  const launchRecipientByTaskKey = options?.launchRecipientByTaskKey && typeof options.launchRecipientByTaskKey === 'object'
    ? options.launchRecipientByTaskKey
    : {};
  const launchAssigneesByTaskKey = options?.launchAssigneesByTaskKey && typeof options.launchAssigneesByTaskKey === 'object'
    ? options.launchAssigneesByTaskKey
    : {};
  const hasLaunchRecipientOverride = Object.prototype.hasOwnProperty.call(
    launchRecipientByTaskKey,
    normalizedTask.key
  );
  const hasLaunchAssigneesOverride = Object.prototype.hasOwnProperty.call(
    launchAssigneesByTaskKey,
    normalizedTask.key
  );
  const sendMessageRecipient = isMissionSendMessageTask(normalizedTask)
    ? normalizeMissionPeople(
      hasLaunchRecipientOverride
        ? launchRecipientByTaskKey[normalizedTask.key]
        : existingRunConfig?.sendMessageRecipient
    )[0] || null
    : null;
  const nextAssignees = normalizedTask.handledBy === MISSION_TASK_HANDLED_BY.PEOPLE
    ? normalizeMissionPeople(
      hasLaunchAssigneesOverride
        ? launchAssigneesByTaskKey[normalizedTask.key]
        : normalizedTask.assignees
    )
    : [];

  const nodeKey = String(task?.nodeKey || normalizedTask?.nodeKey || '').trim();

  return {
    ...normalizedTask,
    assignees: nextAssignees,
    latestActivity: buildMissionTaskActivityLine({
      ...normalizedTask,
      assignees: nextAssignees,
      status,
    }),
    ...(nodeKey ? { nodeKey } : {}),
    readyAtMs: normalizeMissionTimestamp(task?.readyAtMs),
    runConfig: isMissionSendMessageTask(normalizedTask)
      ? {
        ...existingRunConfig,
        sendMessageRecipient,
      }
      : existingRunConfig,
    status,
    updatedAtMs: Date.now(),
  };
}

/**
 * buildMissionRun
 *
 * Creates the node-scoped Mission run payload stored on the node document.
 *
 * @param {object} template - Eligible Mission template.
 * @param {object|null} user - Current user summary source.
 * @returns {object} Node-scoped Mission run payload.
 */
function buildMissionRun(template, user, options = {}) {
  const nowMs = Date.now();
  const runEdges = resolveWorkflowRunEdges(template?.tasks, template?.edges);
  const nextTasks = recomputeMissionRunTasks(
    (Array.isArray(template.tasks) ? template.tasks : [])
      .map((task) => buildMissionRunTask(task, options)),
    runEdges,
  ).map((task) => ({
    ...task,
    // Stamp actionable tasks at launch so duration countdowns have a real start.
    readyAtMs: (task.status === 'waiting' || task.status === 'skipped')
      ? 0
      : (normalizeMissionTimestamp(task?.readyAtMs) || nowMs),
  }));

  return {
    edges: runEdges,
    key: template.key,
    launchedAtMs: nowMs,
    launchedByKey: String(user?.key || '').trim(),
    taskEvents: [],
    tasks: applyMissionTaskActivity(nextTasks, []),
    templateDescription: String(template.description || ''),
    templateTitle: String(template.title || '').trim(),
    updatedAtMs: nowMs,
  };
}

function formatMissionBotFieldValue(value) {
  if (value == null || value === '') return 'None';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value.trim() || 'None';
  if (Array.isArray(value)) {
    const parts = value.map((entry) => formatMissionBotFieldValue(entry)).filter(Boolean);
    return parts.length ? parts.join(', ') : 'None';
  }
  if (typeof value?.toDate === 'function') {
    try {
      return value.toDate().toLocaleString();
    } catch (error) {
      return 'None';
    }
  }
  if (typeof value === 'object') {
    return String(value?.title || value?.name || value?.label || value?.email || '').trim() || 'None';
  }

  return String(value);
}

function buildMissionBotRecordDetails(node, typeDef, fieldKeys) {
  const normalizedFieldKeys = Array.from(new Set(
    (Array.isArray(fieldKeys) ? fieldKeys : [])
      .map((entry) => String(entry || '').trim())
      .filter(Boolean)
  ));
  if (!normalizedFieldKeys.length) return [];

  const schemaFields = Array.isArray(typeDef?.schema?.fields) ? typeDef.schema.fields : [];

  return normalizedFieldKeys.map((fieldKey) => {
    if (fieldKey === MISSION_MESSAGE_DETAIL_TITLE_KEY) {
      const titleValue = String(node?.title || node?.name || '').trim();
      return `Title: ${formatMissionBotFieldValue(titleValue)}`;
    }

    const fieldDef = schemaFields.find((field) => {
      const candidateKey = String(field?.name || field?.key || '').trim();
      return candidateKey === fieldKey;
    }) || null;
    const fieldLabel = String(fieldDef?.label || fieldDef?.title || fieldKey).trim() || fieldKey;
    const fieldValue = resolveSchemaFieldValue({
      dataSection: typeDef?.dataSection,
      fieldDef: fieldDef || { key: fieldKey, name: fieldKey },
      item: node,
      typeKey: typeDef?.key || node?.type || '',
    });

    return `${fieldLabel}: ${formatMissionBotFieldValue(fieldValue)}`;
  }).filter(Boolean);
}

function buildMissionBotSendMessageText({ node, task, template, typeDef }) {
  const sendMessage = task?.missionBot?.config?.sendMessage || {};
  const baseMessage = String(sendMessage?.message || '').trim();
  const recordDetails = buildMissionBotRecordDetails(node, typeDef, sendMessage?.includeRecordFieldKeys);
  const nodeTitle = String(node?.title || node?.name || '').trim();
  const contextLine = nodeTitle ? `${template?.title || 'Mission'} for ${nodeTitle}` : String(template?.title || 'Mission').trim();

  return [
    contextLine,
    baseMessage,
    recordDetails.length ? ['Record details:', ...recordDetails].join('\n') : '',
  ].filter(Boolean).join('\n\n');
}

/**
 * isMissionRunComplete
 *
 * Returns whether every task on a launched Mission run is complete.
 *
 * @param {object|null} missionRun - Normalized Mission run.
 * @returns {boolean} Whether the mission is complete.
 */
function isMissionRunComplete(missionRun) {
  const tasks = Array.isArray(missionRun?.tasks) ? missionRun.tasks : [];
  return tasks.length > 0 && tasks.every((task) => {
    const status = String(task?.status || '').trim();
    return status === 'complete' || status === 'skipped';
  });
}

/**
 * isMissionTaskActionableForUser
 *
 * Returns whether an unfinished people task assigned to the user needs attention now.
 *
 * @param {object|null} task - Mission run task.
 * @param {object|null} user - Current signed-in user.
 * @returns {boolean} Whether the task is actionable for the user.
 */
function isMissionTaskActionableForUser(task, user) {
  if (task?.handledBy !== MISSION_TASK_HANDLED_BY.PEOPLE) return false;
  if (!isMissionTaskAssignee(task, user)) return false;

  const status = String(task?.status || '').trim();
  if (!status || status === 'complete' || status === 'waiting' || status === 'skipped') {
    return false;
  }

  return true;
}

/**
 * hasOpenMissionRuns
 *
 * Returns whether the node has any launched Mission run that is not complete.
 *
 * @param {object|null} node - Current anchor node.
 * @returns {boolean} Whether at least one open Mission run exists.
 */
function hasOpenMissionRuns(node) {
  const missionRuns = node?.missionRuns;
  if (!missionRuns || typeof missionRuns !== 'object') return false;

  return Object.values(missionRuns).some((run) => {
    if (!run || typeof run !== 'object') return false;
    return !isMissionRunComplete(run);
  });
}

/**
 * getNodeWorkflowPercentComplete
 *
 * Returns 0–100 completion for the node's active launched workflow, or null
 * when no workflow run is attached.
 *
 * @param {object|null} node - Node that may have missionRuns.
 * @returns {number|null} Percent complete, or null when no run exists.
 */
function getNodeWorkflowPercentComplete(node) {
  const missionRuns = node?.missionRuns;
  if (!missionRuns || typeof missionRuns !== 'object') return null;

  const runs = Object.values(missionRuns).filter((run) => (
    run
    && typeof run === 'object'
    && Array.isArray(run.tasks)
    && run.tasks.length > 0
  ));
  if (!runs.length) return null;

  const active = runs.find((run) => !isMissionRunComplete(run)) || runs[0];
  return getMissionProgress(active).percent;
}

/**
 * hasUserActionableMissionRuns
 *
 * Returns whether the current user has actionable work on any open Mission run
 * for the node.
 *
 * @param {object|null} node - Current anchor node.
 * @param {object|null} nodeType - Current node type definition.
 * @param {object|null} user - Current signed-in user.
 * @returns {boolean} Whether Missions should be preferred for this user.
 */
function hasUserActionableMissionRuns(node, nodeType, user) {
  if (!node || !user?.key) return false;

  const templates = normalizeEligibleMissionTemplates(nodeType);

  return templates.some((template) => {
    const missionRun = normalizeMissionRun(node?.missionRuns?.[template.key], template);
    if (!missionRun || isMissionRunComplete(missionRun)) return false;

    return (Array.isArray(missionRun.tasks) ? missionRun.tasks : []).some(
      (task) => isMissionTaskActionableForUser(task, user),
    );
  });
}

/**
 * normalizeEligibleMissionTemplates
 *
 * Normalizes the current type-level Missions document into eligible Mission templates.
 *
 * @param {object|null} typeDef - Current node type definition.
 * @returns {object[]} Eligible Mission templates.
 */
function normalizeEligibleMissionTemplates(typeDef) {
  return normalizeMissionTemplateList(typeDef?.missions, {
    legacyTemplateKey: buildNodeMissionTemplateKey(typeDef?.key || typeDef?.type || ''),
  })
    .map((template) => {
      if (!String(template?.title || '').trim() && (!template?.tasks || template.tasks.length === 0)) {
        return null;
      }

      return {
        description: String(template?.description || ''),
        edges: normalizeWorkflowEdges(template?.edges),
        iconKey: String(template?.iconKey || '').trim() || 'account_tree',
        key: String(template?.key || '').trim(),
        tasks: Array.isArray(template?.tasks) ? template.tasks : [],
        title: String(template?.title || '').trim() || 'Untitled Playbook',
        ...(template?.isBaseWorkflow === true ? { isBaseWorkflow: true, scope: 'BASE' } : {}),
      };
    })
    .filter(Boolean);
}

/**
 * normalizeMissionRun
 *
 * Normalizes a stored node-scoped Mission run against the current template.
 *
 * @param {object|null} run - Stored Mission run.
 * @param {object|null} template - Eligible Mission template.
 * @returns {object|null} Normalized Mission run.
 */
function normalizeMissionRun(run, template) {
  if (!run || typeof run !== 'object' || !template) return null;

  const templateTaskMap = new Map(
    (Array.isArray(template.tasks) ? template.tasks : []).map((task) => [String(task?.key || '').trim(), task])
  );

  const nextTasks = (Array.isArray(run?.tasks) ? run.tasks : [])
    .map((task) => {
      const normalizedTask = buildMissionRunTask({
        ...templateTaskMap.get(String(task?.key || '').trim()),
        ...task,
      });

      const status = String(task?.status || normalizedTask.status || '').trim() || 'ready';

      const nodeKey = String(task?.nodeKey || normalizedTask?.nodeKey || '').trim();

      const completionOutcomeKey = String(task?.completionOutcomeKey || '').trim();

      return {
        ...normalizedTask,
        ...(nodeKey ? { nodeKey } : {}),
        ...(completionOutcomeKey ? { completionOutcomeKey } : {}),
        readyAtMs: normalizeMissionTimestamp(task?.readyAtMs || normalizedTask?.readyAtMs),
        status,
        updatedAtMs: Number(task?.updatedAtMs || run?.updatedAtMs || 0) || Date.now(),
      };
    })
    .filter((task) => String(task?.key || '').trim());

  const taskEvents = normalizeMissionTaskEvents(run?.taskEvents);
  const runEdges = resolveWorkflowRunEdges(
    nextTasks,
    Array.isArray(run?.edges) && run.edges.length > 0 ? run.edges : template?.edges,
  );

  return {
    edges: runEdges,
    key: template.key,
    launchedAtMs: Number(run?.launchedAtMs || 0) || 0,
    launchedByKey: String(run?.launchedByKey || '').trim(),
    taskEvents,
    tasks: applyMissionTaskActivity(recomputeMissionRunTasks(nextTasks, runEdges), taskEvents),
    templateDescription: String(run?.templateDescription || template.description || ''),
    templateTitle: String(run?.templateTitle || template.title || '').trim(),
    updatedAtMs: Number(run?.updatedAtMs || 0) || 0,
  };
}

/**
 * isMissionTaskSkipped
 *
 * True when a run task is off the path taken for this workflow.
 *
 * @param {object|null|undefined} task - Mission run task.
 * @returns {boolean}
 */
function isMissionTaskSkipped(task) {
  return String(task?.status || '').trim() === 'skipped';
}

/**
 * getMissionActivePathTasks
 *
 * Tasks on the path taken so far and still reachable ahead. Bypassed
 * alternate branches (skipped) are excluded.
 *
 * @param {object|null} run - Normalized Mission run.
 * @returns {object[]} Active-path tasks in run order.
 */
function getMissionActivePathTasks(run) {
  const tasks = Array.isArray(run?.tasks) ? run.tasks : [];
  return tasks.filter((task) => !isMissionTaskSkipped(task));
}

/**
 * getMissionProgress
 *
 * Calculates completion counts and percentage for the active path of a
 * Mission run (skipped alternate branches are excluded from both counts).
 *
 * @param {object|null} run - Normalized Mission run.
 * @returns {object} Mission progress summary.
 */
function getMissionProgress(run) {
  const tasks = getMissionActivePathTasks(run);
  const total = tasks.length;
  const complete = tasks.filter((task) => (
    String(task?.status || '').trim() === 'complete'
  )).length;
  const percent = total > 0 ? Math.round((complete / total) * 100) : 0;

  return {
    complete,
    percent,
    total,
  };
}

/**
 * getMissionRunStatus
 *
 * Derives the top-level Mission status from the run task states.
 *
 * @param {object|null} run - Normalized Mission run.
 * @returns {string} One of the node Mission status keys.
 */
function getMissionRunStatus(run) {
  const tasks = Array.isArray(run?.tasks) ? run.tasks : [];
  if (!tasks.length) return NODE_MISSION_STATUS.NOT_STARTED;

  const statuses = tasks.map((task) => String(task?.status || '').trim());

  if (
    statuses.length > 0
    && statuses.every((status) => status === 'complete' || status === 'skipped')
  ) {
    return NODE_MISSION_STATUS.COMPLETE;
  }

  if (statuses.some((status) => status === 'failed' || status === 'blocked')) {
    return NODE_MISSION_STATUS.BLOCKED;
  }

  if (statuses.some((status) => status === 'inProgress' || status === 'running' || status === 'complete')) {
    return NODE_MISSION_STATUS.IN_PROGRESS;
  }

  return NODE_MISSION_STATUS.NOT_STARTED;
}

/**
 * getMissionStatusBadgeTheme
 *
 * Resolves Mission badge colors from the current theme and status.
 *
 * @param {object} theme - Active theme object.
 * @param {string} status - Mission status key.
 * @returns {object} Badge background and foreground colors.
 */
function getMissionStatusBadgeTheme(theme, status) {
  if (status === NODE_MISSION_STATUS.COMPLETE) {
    return {
      backgroundColor: theme.green,
      color: theme.highlightForeground,
    };
  }

  if (status === NODE_MISSION_STATUS.BLOCKED) {
    return {
      backgroundColor: theme.red,
      color: theme.highlightForeground,
    };
  }

  if (status === NODE_MISSION_STATUS.IN_PROGRESS) {
    return {
      backgroundColor: theme.blue,
      color: theme.highlightForeground,
    };
  }

  return {
    backgroundColor: theme.orange,
    color: theme.highlightForeground,
  };
}

/**
 * buildNextMissionRunsPatch
 *
 * Builds the full node-level Mission run patch for a single template update.
 *
 * @param {object} currentMissionRuns - Existing node mission runs.
 * @param {string} templateKey - Target template key.
 * @param {object} nextRun - Next run payload.
 * @returns {object} Full missionRuns patch object.
 */
function buildNextMissionRunsPatch(currentMissionRuns, templateKey, nextRun) {
  return {
    ...(currentMissionRuns && typeof currentMissionRuns === 'object' ? currentMissionRuns : {}),
    [templateKey]: nextRun,
  };
}

/**
 * buildMissionRunsWithoutKey
 *
 * Returns missionRuns with one template run removed (for cancel / reset).
 *
 * @param {object} currentMissionRuns - Existing node mission runs.
 * @param {string} templateKey - Template key to remove.
 * @returns {object} Remaining missionRuns map.
 */
function buildMissionRunsWithoutKey(currentMissionRuns, templateKey) {
  const normalizedKey = String(templateKey || '').trim();
  const next = {
    ...(currentMissionRuns && typeof currentMissionRuns === 'object' ? currentMissionRuns : {}),
  };

  if (normalizedKey) {
    delete next[normalizedKey];
  }

  return next;
}

export {
  NODE_MISSION_STATUS,
  NODE_MISSION_STATUS_LABELS,
  NODE_MISSION_TASK_EVENT_TYPE,
  appendDerivedMissionTaskEvents,
  appendStatusTransitionTaskEvents,
  applyMissionTaskActivity,
  buildMissionBotRecordDetails,
  buildMissionBotSendMessageText,
  buildMissionRun,
  buildMissionRunTask,
  buildMissionTaskActivityFromEvent,
  buildMissionTaskActivityLine,
  buildMissionTaskEvent,
  buildMissionTaskInitialStatus,
  buildMissionTaskNudgeDetail,
  buildMissionRunsWithoutKey,
  buildNextMissionRunsPatch,
  buildNodeMissionTemplateKey,
  buildReopenMissionTaskConfirmMessage,
  canBlockMissionTask,
  canMarkCompleteMissionTask,
  canNudgeMissionTask,
  canReopenMissionTask,
  canUnblockMissionTask,
  canProcessMissionBotTask,
  collectMissionTaskReopenCascadeTasks,
  collectTransitiveDependentTaskKeys,
  deriveMissionTaskStatus,
  findFirstHumanActionableTaskInPrerequisiteChain,
  formatMissionDateTime,
  formatMissionTaskEventTime,
  formatMissionUpdatedAt,
  getMissionActivePathTasks,
  getMissionProgress,
  getMissionRunStatus,
  getNodeWorkflowPercentComplete,
  isMissionTaskSkipped,
  getMissionRunTaskLaunchRecipient,
  getMissionStatusBadgeTheme,
  getMissionTaskAssigneeLabels,
  getMissionTaskEvents,
  getMissionTaskRevisionLabel,
  getMissionTaskStatusPresentation,
  getMissionWaitingOnTasks,
  hasOpenMissionRuns,
  hasUserActionableMissionRuns,
  isMissionRunComplete,
  isMissionSendMessageTask,
  isMissionTaskActionableForUser,
  isMissionTaskAssignee,
  isMissionTaskAwaitingHumanAction,
  normalizeEligibleMissionTemplates,
  normalizeMissionRun,
  normalizeMissionTaskEvents,
  normalizeMissionTimestamp,
  reopenMissionRunTask,
  recomputeMissionRunTasks,
};
